<template>
  <div id="settings" class="settings">
    <div
      id="setting-background"
      class="settings__background"
      @click="handleMenuCloseClick()"
    />

    <div class="settings__overlay">
      <button
        @click="handleMenuCloseClick()"
        class="settings__overlay__close-btn"
      >
        <Cross class="settings__overlay__close-btn__icon" />
      </button>

      <div class="settings__overlay__header__container">
        <div
          class="settings__overlay__header__subheader settings__overlay__header__subheader--green-heavy"
        >
          {{ $t("MainSettingSidebar.label.subHeader") }}
        </div>
        <div class="settings__overlay__header">
          {{ $t("MainSettingSidebar.label.header") }}
        </div>
      </div>

      <div class="settings__overlay__inner" v-if="!hideThemeToggle">
        <Toggle
          :first-option="$t('MainSettingSidebar.label.light')"
          :second-option="$t('MainSettingSidebar.label.dark')"
          first-icon-type="Sun"
          second-icon-type="Moon"
          :isSecondOptionSelected="globalStore.getIsLightMode"
          @update:checked="handleThemeToggle"
        />
      </div>

      <LanguageInput />
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRoute } from "vue-router";
import gsap from "gsap";

import Cross from "@/components/icons/Cross.vue";
import LanguageInput from "@/components/ui/inputs/LanguageInput.vue";
import Toggle from "@/components/ui/toggles/Toggle.vue";
import useTheme from "@/composables/ui/useTheme";
import { useGlobalStore } from "@/stores/globalStore";

const route = useRoute();
const hideThemeToggle = ref(false);
const globalStore = useGlobalStore();
const { updateThemeFromStore, themeIsFromUrl } = useTheme();

const handleMenuCloseClick = () => {
  globalStore.toggleSettings();
};

const handleThemeToggle = () => {
  globalStore.toggleTheme();
  updateThemeFromStore();
};

watch(
  () => route.query,
  async () => {
    themeIsFromUrl(route.query).then((isThemeQuery) => {
      if (isThemeQuery) {
        hideThemeToggle.value = true;
      } else {
        hideThemeToggle.value = false;
      }
    });
  },
  { deep: true }
);

watch(
  () => globalStore.global.showSettings,
  () => {
    const sidebarEl = ".settings__overlay";
    const settingBackground = "#setting-background";
    const animationTimeline = gsap.timeline();

    if (globalStore.global.showSettings) {
      animationTimeline.to("#settings", { display: "block" });
      animationTimeline.to(settingBackground, { duration: 0.5, opacity: 0.8 });
      animationTimeline.to(sidebarEl, {
        x: "0",
        duration: 0.5,
        delay: -0.75,
        ease: "power3.inOut",
      });
    } else {
      animationTimeline.to(settingBackground, { duration: 0.5, opacity: 0 });
      animationTimeline.to(sidebarEl, {
        x: "100%",
        duration: 0.5,
        ease: "power3.inOut",
        delay: -0.5,
      });
      animationTimeline.to("#settings", { display: "none" });
    }
  }
);
</script>

<style lang="scss">
.settings {
  position: fixed;
  width: 100vw;
  max-width: 100vw;
  max-height: 100vh;
  height: 100vh;
  opacity: 1;
  overflow: hidden;
  box-sizing: border-box;
  left: 0;
  top: 0;
  display: none;

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: black;
    flex: 1;
    opacity: 0;
    display: none;

    @include breakpoint($min-m) {
      display: block;
    }
  }

  &__overlay {
    width: 100%;
    height: 100vh;
    padding: $padding-lg;
    background: var(--settings-overlay-background);
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: $formFieldSpacing;
    position: absolute;
    top: 0;
    right: 0;
    transform: translateX(100%);

    @include breakpoint($min-m) {
      overflow-y: hidden;
      max-width: 480px;
      height: 100vh;
      width: 100vw;
      display: flex;
      flex-direction: column;
    }

    @media screen and (max-height: $screen-sm) {
      overflow-y: scroll;
    }

    .settings__overlay__close-btn {
      margin-left: auto;
      margin-bottom: $padding-std;
      border: none;
      background: none;
      cursor: pointer;

      &__icon {
        g {
          transition: var(--button-icon-transition);

          path {
            stroke: var(--icon-color-default);
          }
        }

        &:hover {
          g {
            opacity: 1;
          }
        }
      }
    }

    &__header {
      font-weight: 900;
      font-size: $font-sub-jumbo;
      padding: calc($padding-sml / 2) 0;

      &__container {
        margin-bottom: $padding-lg;
      }

      &__subheader {
        font-weight: 500;
        font-size: $font-subhead;

        &--green-heavy {
          color: var(--primary);
        }

        &--subtle {
          opacity: 0.7;
          font-weight: 400;
        }
      }
    }
  }
}
</style>
